import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../Spinner';
import {
  getEventTypeLabel,
  getEventTypeColor,
  getActionLabel,
  getActionColor,
  getStatusLabel,
  getStatusColor,
  getResourceTypeLabel,
  eventTypeCategories,
} from './auditEventTypes';

export default function AuditLogTable() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filter state
  const [filters, setFilters] = useState({
    event_type: '',
    user_id: '',
    action: '',
    status: '',
    resource_type: '',
    ip_address: '',
    start_date: '',
    end_date: '',
    search: '',
  });

  // Filter options from API
  const [filterOptions, setFilterOptions] = useState({
    event_types: [],
    actions: [],
    resource_types: [],
    statuses: ['success', 'failed', 'denied'],
  });

  // Filters panel visibility
  const [showFilters, setShowFilters] = useState(true);

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await fetch(`${API_URL}/admin/audit/filter-options`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setFilterOptions(data);
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    fetchFilterOptions();
  }, [token]);

  // Fetch audit events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });

      // Add active filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`${API_URL}/admin/audit/events?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Errore nel caricamento degli eventi');
      }

      const data = await response.json();
      setEvents(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, page, perPage, filters]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filters change
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      event_type: '',
      user_id: '',
      action: '',
      status: '',
      resource_type: '',
      ip_address: '',
      start_date: '',
      end_date: '',
      search: '',
    });
    setPage(1);
  };

  // Export CSV
  const handleExport = async () => {
    if (!filters.start_date || !filters.end_date) {
      alert('Per esportare, seleziona un intervallo di date (massimo 90 giorni)');
      return;
    }

    try {
      const params = new URLSearchParams({
        start_date: filters.start_date,
        end_date: filters.end_date,
      });

      // Add other active filters
      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.action) params.append('action', filters.action);
      if (filters.status) params.append('status', filters.status);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);

      const response = await fetch(`${API_URL}/admin/audit/events/export-csv?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore durante l\'export');
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_log_${filters.start_date}_to_${filters.end_date}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      alert(err.message);
    }
  };

  // Toggle row expansion
  const toggleRow = (eventId) => {
    setExpandedRow(expandedRow === eventId ? null : eventId);
  };

  // Format details for display
  const formatDetails = (details) => {
    if (!details) return null;
    return JSON.stringify(details, null, 2);
  };

  // Get summary from details
  const getDetailsSummary = (details) => {
    if (!details) return '-';

    // Try to extract meaningful info
    if (details.email) return details.email;
    if (details.title) return details.title;
    if (details.target_email) return details.target_email;
    if (details.role) return `Ruolo: ${details.role}`;
    if (details.reason) return details.reason;
    if (details.changes) {
      const keys = Object.keys(details.changes);
      return `Modifiche: ${keys.join(', ')}`;
    }

    // Rate limit event summary
    if (details.blocked_action) {
      const actionLabels = {
        user_login: 'Login',
        user_registered: 'Registrazione',
        hike_registration: 'Iscrizione gita',
        hike_unregistration: 'Disiscrizione gita',
        password_reset_requested: 'Reset password',
      };
      const actionLabel = actionLabels[details.blocked_action] || details.blocked_action;
      return `${actionLabel} (${details.attempt_count}/${details.max_attempts})`;
    }

    return 'Clicca per dettagli';
  };

  // Get user display name
  const getUserDisplay = (event) => {
    if (event.user_email) {
      const name = [event.user_nome, event.user_cognome].filter(Boolean).join(' ');
      return name ? `${name} (${event.user_email})` : event.user_email;
    }
    if (event.user_id) return `ID: ${event.user_id}`;

    // For rate limit events, show the identifier from details
    if (event.event_type === 'rate_limit_exceeded' && event.details) {
      const { identifier, identifier_type } = event.details;
      if (identifier) {
        if (identifier_type === 'email') return identifier;
        if (identifier_type === 'ip') return `IP: ${identifier}`;
        if (identifier_type === 'user_id') return `ID: ${identifier}`;
        return identifier;
      }
    }

    return '-';
  };

  // Get resource link
  const getResourceLink = (event) => {
    if (!event.resource_type || !event.resource_id) return null;

    if (event.resource_type === 'hike') {
      return `/hikes/${event.resource_id}`;
    }
    // Add more resource types as needed
    return null;
  };

  if (loading && events.length === 0) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters Panel */}
      <div className="bg-white rounded-lg shadow">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full px-4 py-3 flex items-center justify-between text-left font-medium text-gray-700 hover:bg-gray-50"
        >
          <span>Filtri</span>
          <svg
            className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showFilters && (
          <div className="p-4 border-t space-y-4">
            {/* Row 1: Date range and search */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Data inizio</label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Data fine</label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Indirizzo IP</label>
                <input
                  type="text"
                  placeholder="es. 192.168"
                  value={filters.ip_address}
                  onChange={(e) => handleFilterChange('ip_address', e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Cerca nei dettagli</label>
                <input
                  type="text"
                  placeholder="Ricerca libera..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
            </div>

            {/* Row 2: Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tipo evento</label>
                <select
                  value={filters.event_type}
                  onChange={(e) => handleFilterChange('event_type', e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="">Tutti</option>
                  {Object.entries(eventTypeCategories).map(([category, types]) => (
                    <optgroup key={category} label={category}>
                      {types.map(type => (
                        <option key={type} value={type}>
                          {getEventTypeLabel(type)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Azione</label>
                <select
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="">Tutte</option>
                  {filterOptions.actions.map(action => (
                    <option key={action} value={action}>
                      {getActionLabel(action)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Stato</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="">Tutti</option>
                  {filterOptions.statuses.map(status => (
                    <option key={status} value={status}>
                      {getStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tipo risorsa</label>
                <select
                  value={filters.resource_type}
                  onChange={(e) => handleFilterChange('resource_type', e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="">Tutti</option>
                  {filterOptions.resource_types.map(type => (
                    <option key={type} value={type}>
                      {getResourceTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">ID Utente</label>
                <input
                  type="number"
                  placeholder="ID utente"
                  value={filters.user_id}
                  onChange={(e) => handleFilterChange('user_id', e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Pulisci filtri
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              >
                Esporta CSV
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Results count */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>Mostrando {events.length} di {total} eventi</span>
        <div className="flex items-center gap-2">
          <span>Per pagina:</span>
          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
            className="px-2 py-1 border rounded"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Data/Ora</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Tipo Evento</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Utente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Azione</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Stato</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Risorsa</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">IP</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Dettagli</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {events.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    Nessun evento trovato
                  </td>
                </tr>
              ) : (
                events.map((event) => (
                  <>
                    <tr
                      key={event.id}
                      className={`hover:bg-gray-50 cursor-pointer ${expandedRow === event.id ? 'bg-blue-50' : ''}`}
                      onClick={() => toggleRow(event.id)}
                    >
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {new Date(event.created_at).toLocaleString('it-IT')}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getEventTypeColor(event.event_type)}`}>
                          {getEventTypeLabel(event.event_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getUserDisplay(event)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getActionColor(event.action)}`}>
                          {getActionLabel(event.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(event.status)}`}>
                          {getStatusLabel(event.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {event.resource_type ? (
                          <span>
                            {getResourceTypeLabel(event.resource_type)}
                            {event.resource_id && (
                              <>
                                {' #'}
                                {getResourceLink(event) ? (
                                  <a
                                    href={getResourceLink(event)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-blue-600 hover:underline"
                                  >
                                    {event.resource_id}
                                  </a>
                                ) : (
                                  event.resource_id
                                )}
                              </>
                            )}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {event.ip_address || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          {getDetailsSummary(event.details)}
                          {event.details && (
                            <svg
                              className={`w-4 h-4 transition-transform ${expandedRow === event.id ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </span>
                      </td>
                    </tr>
                    {/* Expanded row for details */}
                    {expandedRow === event.id && event.details && (
                      <tr key={`${event.id}-details`}>
                        <td colSpan={8} className="px-4 py-4 bg-gray-50">
                          <div className="text-sm">
                            <div className="font-medium text-gray-700 mb-2">Dettagli completi:</div>
                            <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
                              {formatDetails(event.details)}
                            </pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            className="px-4 py-2 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Precedente
          </button>
          <span className="text-sm text-gray-600">
            Pagina {page} di {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
            className="px-4 py-2 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Successiva
          </button>
        </div>
      )}

      {/* Loading overlay */}
      {loading && events.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-10">
          <Spinner />
        </div>
      )}
    </div>
  );
}
